import type { Ref } from 'react';
import { createContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { ModalOverlayProps as AriaModalOverlayProps } from 'react-aria-components/Modal';
import { Modal as AriaModal, ModalOverlay as AriaModalOverlay } from 'react-aria-components/Modal';
import type { ContextValue } from 'react-aria-components/slots';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { useLPContextProps } from './utils';

import styles from './styles/Modal.module.css';

const modalStyles = cva(styles.base, {
	variants: {
		size: {
			small: undefined,
			medium: undefined,
			large: styles.large,
		},
		variant: {
			default: styles.default,
			drawer: styles.drawer,
		},
	},
	defaultVariants: {
		size: 'medium',
		variant: 'default',
	},
	compoundVariants: [
		{
			variant: 'default',
			size: 'small',
			className: styles.defaultSmall,
		},
		{
			variant: 'default',
			size: 'medium',
			className: styles.defaultMedium,
		},
		{
			variant: 'drawer',
			size: 'small',
			className: styles.drawerSmall,
		},
		{
			variant: 'drawer',
			size: 'medium',
			className: styles.drawerMedium,
		},
	],
});
const modalOverlayStyles = cva(styles.overlay);

interface ModalProps extends AriaModalOverlayProps, VariantProps<typeof modalStyles> {
	ref?: Ref<HTMLDivElement>;
}

interface ModalOverlayProps extends AriaModalOverlayProps {
	ref?: Ref<HTMLDivElement>;
}

const ModalContext = createContext<ContextValue<ModalProps, HTMLDivElement>>(null);

/**
 * A modal is an overlay element which blocks interaction with elements outside it.
 *
 * https://react-spectrum.adobe.com/react-aria/Modal.html
 */
const Modal = ({ ref, ...props }: ModalProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, ModalContext);
	const { size = 'medium', variant = 'default' } = mergedProps;

	return (
		<AriaModal
			{...mergedProps}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				modalStyles({ ...renderProps, size, variant, className }),
			)}
		/>
	);
};

/**
 * A ModalOverlay is a wrapper for a Modal which allows customizing the backdrop element.
 */
const ModalOverlay = ({ isDismissable = true, ref, ...props }: ModalOverlayProps) => {
	return (
		<AriaModalOverlay
			isDismissable={isDismissable}
			shouldCloseOnInteractOutside={(element) => {
				// Don't close when the element being checked is HTML or BODY.
				// This indicates a portal interaction (e.g., spawning a toast from a button).
				//
				// Why this is safe:
				// - When users click the modal backdrop, the element is the overlay <div>, NOT HTML/BODY
				// - HTML/BODY only appear here during React portal rendering edge cases
				// - This prevents the modal from incorrectly closing when triggering toasts from modal buttons
				// - Legitimate outside clicks (backdrop, other UI) still close the modal as expected
				const isRootElement = element.tagName === 'HTML' || element.tagName === 'BODY';

				return !isRootElement;
			}}
			{...props}
			ref={ref}
			className={composeRenderProps(props.className, (className, renderProps) =>
				modalOverlayStyles({ ...renderProps, className }),
			)}
		/>
	);
};

export { Modal, ModalContext, ModalOverlay, modalOverlayStyles, modalStyles };
export type { ModalProps, ModalOverlayProps };
